import React from "react";
import * as PAGES from "../pages/pages";
import { ROUTES } from "./routes";

export type RouteDef = {
  path: string;
  element: React.ReactElement;
  title?: string;
};

const route = (
  path: string,
  element: React.ReactElement,
  title?: string,
): RouteDef => ({ path, element, title });

// Routes outside the auth-required layout.
export const PUBLIC_ROUTES: RouteDef[] = [
  route(ROUTES.LOGIN, <PAGES.Login />, "Sign in"),
  route(ROUTES.SIGNUP, <PAGES.SignUp />, "Create account"),
  route(ROUTES.VERIFICATION_INSTRUCTIONS, <PAGES.VerificationInstructions />, "Verify email"),
  route(ROUTES.VERIFY_OTP, <PAGES.VerifyOtp />, "Verify account"),
];

// Routes inside RequireAuth → DashboardLayout (NavBar + SideBar + providers).
export const PROTECTED_ROUTES: RouteDef[] = [
  // ─── Home ───
  route(ROUTES.HOME, <PAGES.DashBoard />, "Dashboard"),

  // ─── Regions ───
  route(ROUTES.REGIONS, <PAGES.Regions />, "Continent Regions"),
  route(ROUTES.REGION_MANAGE, <PAGES.RegionOverview />, "Overview"),
  route(ROUTES.REGION_ADD, <PAGES.AddRegionComp />, "Add Region"),
  route(ROUTES.REGION_EDIT, <PAGES.EditRegionComp />, "Edit Region"),
  route(ROUTES.REGION_DASH, <PAGES.SingleRegionDashboard />, "Region Dashboard"),

  // ─── Countries ───
  route(ROUTES.COUNTRIES, <PAGES.Countries />, "Countries"),
  route(ROUTES.COUNTRIES_DASH, <PAGES.CountriesDashboard />, "Countries Dashboard"),
  route(ROUTES.COUNTRIES_DASH_ID, <PAGES.CountryDashboard />, "Country Dashboard"),
  route(ROUTES.COUNTRIES_ADD, <PAGES.AddCountry />, "Add Country"),
  route(ROUTES.COUNTRIES_EDIT, <PAGES.EditCountry />, "Edit Country"),

  // ─── Levels ───
  route(ROUTES.LEVELS_FIRST, <PAGES.Levels />, "First Levels"),
  route(ROUTES.LEVELS_SECOND, <PAGES.Levels />, "Second Levels"),
  route(ROUTES.LEVELS_THIRD, <PAGES.Levels />, "Third Levels"),
  route(ROUTES.LEVELS_FOURTH, <PAGES.Levels />, "Fourth Levels"),
  route(ROUTES.LEVELS_DASH, <PAGES.LevelsDashboard />, "Levels Dashboard"),
  route(ROUTES.LEVEL_DASH, <PAGES.LevelDashboard />, "Level Dashboard"),
  route(ROUTES.LEVEL_ADD, <PAGES.AddLevel />, "Add Level"),
  route(ROUTES.LEVEL_EDIT, <PAGES.EditLevel />, "Edit Level"),

  // ─── Cities ───
  route(ROUTES.CITIES, <PAGES.Cities />, "Cities"),
  route(ROUTES.CITIES_DASH, <PAGES.CitiesDashboard />, "Cities Dashboard"),
  route(ROUTES.CITY_DASH, <PAGES.CityDashboard />, "City Dashboard"),

  // ─── DoSee data ───
  route(ROUTES.DATA, <PAGES.DataContent />, "Data"),

  route(ROUTES.DATA_EVENT, <PAGES.Events />, "Events"),
  route(ROUTES.DATA_EVENT_ADD, <PAGES.AddEvent />, "Add Event"),
  route(ROUTES.DATA_EVENT_EDIT, <PAGES.EditEvent />, "Edit Event"),

  route(ROUTES.DATA_ATTR, <PAGES.Attractions />, "Attractions"),
  route(ROUTES.DATA_ATTR_ADD, <PAGES.AddAttraction />, "Add Attraction"),
  route(ROUTES.DATA_ATTR_VIEW, <PAGES.ViewAttraction />, "View Attraction"),
  route(ROUTES.DATA_ATTR_EDIT, <PAGES.EditAttraction />, "Edit Attraction"),

  route(ROUTES.DATA_BEST, <PAGES.BestOfAfrica />, "Best of Africa"),
  route(ROUTES.DATA_BEST_ADD, <PAGES.AddBestOfAfrica />, "Add Best of Africa"),
  route(ROUTES.DATA_BEST_VIEW, <PAGES.ViewBestOfAfrica />, "View Best of Africa"),
  route(ROUTES.DATA_BEST_EDIT, <PAGES.EditBestOfAfrica />, "Edit Best of Africa"),

  route(ROUTES.DATA_CINEMA, <PAGES.Cinema />, "Cinema"),
  route(ROUTES.DATA_CINEMA_ADD, <PAGES.AddCinema />, "Add Cinema"),
  route(ROUTES.DATA_CINEMA_VIEW, <PAGES.ViewCinema />, "View Cinema"),
  route(ROUTES.DATA_CINEMA_EDIT, <PAGES.EditCinema />, "Edit Cinema"),

  route(ROUTES.DATA_MUSIC, <PAGES.Music />, "Music"),
  route(ROUTES.DATA_MUSIC_ADD, <PAGES.AddMusic />, "Add Music"),
  route(ROUTES.DATA_MUSIC_VIEW, <PAGES.ViewMusic />, "View Music"),
  route(ROUTES.DATA_MUSIC_EDIT, <PAGES.EditMusic />, "Edit Music"),

  route(ROUTES.DATA_NEWS, <PAGES.News />, "News"),
  route(ROUTES.DATA_NEWS_ADD, <PAGES.AddNews />, "Add News"),
  route(ROUTES.DATA_NEWS_VIEW, <PAGES.ViewNews />, "View News"),
  route(ROUTES.DATA_NEWS_EDIT, <PAGES.EditNews />, "Edit News"),

  route(ROUTES.DATA_SAFARI, <PAGES.Safari />, "Safaris"),
  route(ROUTES.DATA_SAFARI_ADD, <PAGES.AddSafari />, "Add Safari"),
  route(ROUTES.DATA_SAFARI_VIEW, <PAGES.ViewSafari />, "View Safari"),
  route(ROUTES.DATA_SAFARI_EDIT, <PAGES.EditSafari />, "Edit Safari"),

  route(ROUTES.DATA_RESTAU, <PAGES.Restaurant />, "Restaurants"),
  route(ROUTES.DATA_RESTAU_ADD, <PAGES.AddRestaurant />, "Add Restaurant"),
  route(ROUTES.DATA_RESTAU_VIEW, <PAGES.ViewRestaurant />, "View Restaurant"),
  route(ROUTES.DATA_RESTAU_EDIT, <PAGES.EditRestaurant />, "Edit Restaurant"),

  route(ROUTES.DATA_TDO, <PAGES.ThingsToDo />, "Things To Do"),
  route(ROUTES.DATA_TDO_ADD, <PAGES.AddThingsToDo />, "Add Things To Do"),
  route(ROUTES.DATA_TDO_EDIT, <PAGES.EditThingsToDo />, "Edit Things To Do"),

  route(ROUTES.DATA_PARTY, <PAGES.Party />, "Parties"),
  route(ROUTES.DATA_PARTY_ADD, <PAGES.AddParty />, "Add Party"),
  route(ROUTES.DATA_PARTY_EDIT, <PAGES.EditParty />, "Edit Party"),

  route(ROUTES.DATA_NLIFE, <PAGES.Nightlife />, "Night Life"),
  route(ROUTES.DATA_NLIFE_ADD, <PAGES.AddNightlife />, "Add Night Life"),
  route(ROUTES.DATA_NLIFE_EDIT, <PAGES.EditNightlife />, "Edit Night Life"),

  route(ROUTES.DATA_CGUIDE, <PAGES.CityGuides />, "City Guides"),
  route(ROUTES.DATA_CGUIDE_ADD, <PAGES.AddCityGuide />, "Add City Guide"),
  route(ROUTES.DATA_CGUIDE_EDIT, <PAGES.EditCityGuide />, "Edit City Guide"),

  route(ROUTES.DATA_HOTEL, <PAGES.Hotels />, "Hotels"),
  route(ROUTES.DATA_HOTEL_ADD, <PAGES.AddHotel />, "Add Hotel"),
  route(ROUTES.DATA_HOTEL_EDIT, <PAGES.EditHotel />, "Edit Hotel"),

  route(ROUTES.DATA_CLASS, <PAGES.Classes />, "Classes"),
  route(ROUTES.DATA_CLASS_ADD, <PAGES.AddClass />, "Add Class"),
  route(ROUTES.DATA_CLASS_EDIT, <PAGES.EditClass />, "Edit Class"),

  route(ROUTES.DATA_SHOP, <PAGES.Shopping />, "Shopping"),
  route(ROUTES.DATA_SHOP_ADD, <PAGES.AddShopping />, "Add Shopping"),
  route(ROUTES.DATA_SHOP_EDIT, <PAGES.EditShopping />, "Edit Shopping"),

  route(ROUTES.DATA_FOOD, <PAGES.Foods />, "Food"),
  route(ROUTES.DATA_FOOD_ADD, <PAGES.AddFood />, "Add Food"),
  route(ROUTES.DATA_FOOD_EDIT, <PAGES.EditFood />, "Edit Food"),

  route(ROUTES.DATA_BOOK, <PAGES.Books />, "Books"),
  route(ROUTES.DATA_BOOK_ADD, <PAGES.AddBook />, "Add Book"),
  route(ROUTES.DATA_BOOK_EDIT, <PAGES.EditBook />, "Edit Book"),

  route(ROUTES.DATA_NBOR, <PAGES.Neighborhoods />, "Neighborhoods"),
  route(ROUTES.DATA_NBOR_ADD, <PAGES.AddNeighborhood />, "Add Neighborhood"),
  route(ROUTES.DATA_NBOR_EDIT, <PAGES.EditNeighborhood />, "Edit Neighborhood"),

  route(ROUTES.DATA_MINISTRY, <PAGES.Ministry />, "Ministries"),
  route(ROUTES.DATA_MINISTRY_ADD, <PAGES.AddMinistry />, "Add Ministry"),
  route(ROUTES.DATA_MINISTRY_EDIT, <PAGES.EditMinistry />, "Edit Ministry"),

  route(ROUTES.DATA_GA, <PAGES.GettingAround />, "Getting Around"),
  route(ROUTES.DATA_GA_ADD, <PAGES.AddGettingAround />, "Add Getting Around"),
  route(ROUTES.DATA_GA_EDIT, <PAGES.EditGettingAround />, "Edit Getting Around"),

  route(ROUTES.DATA_ART, <PAGES.Arts />, "Arts"),
  route(ROUTES.DATA_ART_ADD, <PAGES.AddArt />, "Add Art"),
  route(ROUTES.DATA_ART_EDIT, <PAGES.EditArt />, "Edit Art"),

  route(ROUTES.DATA_MOVIE, <PAGES.Movies />, "Movies"),
  route(ROUTES.DATA_MOVIE_ADD, <PAGES.AddMovie />, "Add Movie"),
  route(ROUTES.DATA_MOVIE_EDIT, <PAGES.EditMovie />, "Edit Movie"),

  route(ROUTES.DATA_DANCE, <PAGES.Dances />, "Dance"),
  route(ROUTES.DATA_DANCE_ADD, <PAGES.AddDance />, "Add Dance"),
  route(ROUTES.DATA_DANCE_EDIT, <PAGES.EditDance />, "Edit Dance"),

  route(ROUTES.DATA_THEATER, <PAGES.Theaters />, "Theaters"),
  route(ROUTES.DATA_THEATER_ADD, <PAGES.AddTheater />, "Add Theater"),
  route(ROUTES.DATA_THEATER_EDIT, <PAGES.EditTheater />, "Edit Theater"),

  route(ROUTES.DATA_TIPS, <PAGES.TipsAndAdvice />, "Tips & Advice"),
  route(ROUTES.DATA_TIPS_ADD, <PAGES.AddTipsAndAdvice />, "Add Tip"),
  route(ROUTES.DATA_TIPS_EDIT, <PAGES.EditTipsAndAdvice />, "Edit Tip"),

  route(ROUTES.DATA_MUSEUM, <PAGES.Museums />, "Museums"),
  route(ROUTES.DATA_MUSEUM_ADD, <PAGES.AddMuseum />, "Add Museum"),
  route(ROUTES.DATA_MUSEUM_EDIT, <PAGES.EditMuseum />, "Edit Museum"),

  route(ROUTES.DATA_TOUR, <PAGES.TourPackage />, "Tour Packages"),
  route(ROUTES.DATA_TOUR_ADD, <PAGES.AddTourPackage />, "Add Tour Package"),
  route(ROUTES.DATA_TOUR_EDIT, <PAGES.EditTourPackage />, "Edit Tour Package"),

  route(ROUTES.DATA_PROD, <PAGES.Product />, "Products"),
  route(ROUTES.DATA_PROD_ADD, <PAGES.AddProduct />, "Add Product"),
  route(ROUTES.DATA_PROD_EDIT, <PAGES.EditProduct />, "Edit Product"),

  route(ROUTES.DATA_COMPANY, <PAGES.DataCompany />, "Companies"),
  route(ROUTES.DATA_COMPANY_ADD, <PAGES.AddDataCompany />, "Add Company"),
  route(ROUTES.DATA_COMPANY_EDIT, <PAGES.EditDataCompany />, "Edit Company"),

  route(ROUTES.DATA_EMBASSY, <PAGES.Embassy />, "Embassies"),
  route(ROUTES.DATA_EMBASSY_ADD, <PAGES.AddEmbassy />, "Add Embassy"),
  route(ROUTES.DATA_EMBASSY_EDIT, <PAGES.EditEmbassy />, "Edit Embassy"),

  route(ROUTES.DATA_AD, <PAGES.DoseeAds />, "Advertisements"),
  route(ROUTES.DATA_AD_ADD, <PAGES.AddDoseeAd />, "Add Advertisement"),
  route(ROUTES.DATA_AD_EDIT, <PAGES.EditDoseeAd />, "Edit Advertisement"),

  // ─── People ───
  route(ROUTES.DATA_TEAM, <PAGES.Team />, "Team"),
  route(ROUTES.DATA_TEAM_ADD, <PAGES.AddTeam />, "Add Team Member"),
  route(ROUTES.DATA_TEAM_EDIT, <PAGES.EditTeam />, "Edit Team Member"),

  route(ROUTES.DATA_AGENT, <PAGES.Agent />, "Agents"),
  route(ROUTES.DATA_AGENT_ADD, <PAGES.AddAgent />, "Add Agent"),
  route(ROUTES.DATA_AGENT_EDIT, <PAGES.EditAgent />, "Edit Agent"),

  route(ROUTES.DATA_ADMIN, <PAGES.Admin />, "Admins"),
  route(ROUTES.DATA_ADMIN_ADD, <PAGES.AddAdmin />, "Add Admin"),
  route(ROUTES.DATA_ADMIN_EDIT, <PAGES.EditAdmin />, "Edit Admin"),

  route(ROUTES.DATA_USER, <PAGES.User />, "Users"),
  route(ROUTES.DATA_USER_ADD, <PAGES.AddUser />, "Add User"),
  route(ROUTES.DATA_USER_EDIT, <PAGES.EditUser />, "Edit User"),

  // ─── Reference data ───
  route(ROUTES.DATA_TO, <PAGES.TradeOrganization />, "Trade Organizations"),
  route(ROUTES.DATA_TO_ADD, <PAGES.AddTradeOrganization />, "Add Trade Organization"),
  route(ROUTES.DATA_TO_EDIT, <PAGES.EditTradeOrganization />, "Edit Trade Organization"),

  route(ROUTES.DATA_RBLOCKS, <PAGES.RegionalBlocks />, "Regional Blocks"),
  route(ROUTES.DATA_RBLOCKS_ADD, <PAGES.AddRegionalBlocks />, "Add Regional Block"),
  route(ROUTES.DATA_RBLOCKS_EDIT, <PAGES.EditRegionalBlocks />, "Edit Regional Block"),

  route(ROUTES.DATA_PIMAGE, <PAGES.PlugImage />, "Plug Images"),
  route(ROUTES.DATA_PIMAGE_ADD, <PAGES.AddPlugImage />, "Add Plug Image"),
  route(ROUTES.DATA_PIMAGE_EDIT, <PAGES.EditPlugImage />, "Edit Plug Image"),

  route(ROUTES.DATA_CLIMATE, <PAGES.Climate />, "Climate"),
  route(ROUTES.DATA_CLIMATE_ADD, <PAGES.AddClimate />, "Add Climate"),
  route(ROUTES.DATA_CLIMATE_EDIT, <PAGES.EditClimate />, "Edit Climate"),

  route(ROUTES.DATA_EGROUP, <PAGES.EthnicGroup />, "Ethnic Groups"),
  route(ROUTES.DATA_EGROUP_ADD, <PAGES.AddEthnicGroup />, "Add Ethnic Group"),
  route(ROUTES.DATA_EGROUP_EDIT, <PAGES.EditEthnicGroup />, "Edit Ethnic Group"),

  route(ROUTES.DATA_LANG, <PAGES.Language />, "Languages"),
  route(ROUTES.DATA_LANG_ADD, <PAGES.AddLanguage />, "Add Language"),
  route(ROUTES.DATA_LANG_EDIT, <PAGES.EditLanguage />, "Edit Language"),

  route(ROUTES.DATA_REL, <PAGES.Religion />, "Religions"),
  route(ROUTES.DATA_REL_ADD, <PAGES.AddReligion />, "Add Religion"),
  route(ROUTES.DATA_REL_EDIT, <PAGES.EditReligion />, "Edit Religion"),

  // ─── Site content ───
  route(ROUTES.DATA_BANNER, <PAGES.Banner />, "Banners"),
  route(ROUTES.DATA_BANNER_ADD, <PAGES.AddBanner />, "Add Banner"),
  route(ROUTES.DATA_BANNER_EDIT, <PAGES.EditBanner />, "Edit Banner"),

  route(ROUTES.DATA_PAGE, <PAGES.Page />, "Pages"),
  route(ROUTES.DATA_PAGE_ADD, <PAGES.AddPage />, "Add Page"),
  route(ROUTES.DATA_PAGE_EDIT, <PAGES.EditPage />, "Edit Page"),

  route(ROUTES.DATA_BLOG, <PAGES.Blog />, "Blog"),
  route(ROUTES.DATA_BLOG_ADD, <PAGES.AddBlog />, "Add Blog Post"),
  route(ROUTES.DATA_BLOG_EDIT, <PAGES.EditBlog />, "Edit Blog Post"),

  route(ROUTES.DATA_NEWSLETTER, <PAGES.Newsletter />, "Newsletter"),
  route(ROUTES.DATA_AI_CHAT_INFO, <PAGES.AiChatInfo />, "AI Chat Info"),
  route(ROUTES.DATA_AI_CHAT_INFO_EDIT, <PAGES.EditAiChatInfo />, "Edit AI Chat Info"),
  route(ROUTES.DATA_FOOTER, <PAGES.Footer />, "Footer"),

  route(ROUTES.DATA_TERMS, <PAGES.Terms />, "Terms"),
  route(ROUTES.DATA_TERMS_EDIT, <PAGES.EditTerms />, "Edit Terms"),

  route(ROUTES.DATA_PRIVACY, <PAGES.Privacy />, "Privacy"),
  route(ROUTES.DATA_PRIVACY_EDIT, <PAGES.EditPrivacy />, "Edit Privacy"),

  route(ROUTES.DATA_SERVICE, <PAGES.Service />, "Service"),
  route(ROUTES.DATA_SERVICE_EDIT, <PAGES.EditService />, "Edit Service"),

  route(ROUTES.DATA_CONTACT, <PAGES.Contact />, "Contact"),

  route(ROUTES.DATA_ABOUT, <PAGES.About />, "About"),
  route(ROUTES.DATA_ABOUT_EDIT, <PAGES.EditAbout />, "Edit About"),

  route(ROUTES.DATA_EMAIL, <PAGES.EmailTemplates />, "Email Templates"),
  route(ROUTES.DATA_EMAIL_EDIT, <PAGES.EditEmailTemplates />, "Edit Email Template"),

  route(ROUTES.PARTNER, <PAGES.Partner />, "Partners"),
  route(ROUTES.PARTNER_ADD, <PAGES.AddPartner />, "Add Partner"),
  route(ROUTES.PARTNER_EDIT, <PAGES.EditPartner />, "Edit Partner"),

  // ─── Company ───
  route(ROUTES.COMPANY, <PAGES.Company />, "Company"),
  route(ROUTES.COMPANY_ADD, <PAGES.AddCompany />, "Add Company"),
  route(ROUTES.COMPANY_EDIT, <PAGES.EditCompany />, "Edit Company"),
  route(ROUTES.COMPANY_VIEW, <PAGES.ViewCompany />, "View Company"),
  route(ROUTES.COMPANY_DASHBOARD, <PAGES.CompanyDashboard />, "Company Dashboard"),

  // ─── Commerce ───
  route(ROUTES.PAYMENT, <PAGES.Payment />, "Payment"),
  route(ROUTES.CURRENCY, <PAGES.Currency />, "Currency"),
  route(ROUTES.CURRENCY_ADD, <PAGES.AddCurrency />, "Add Currency"),
  route(ROUTES.CURRENCY_EDIT, <PAGES.EditCurrency />, "Edit Currency"),

  // ─── Misc ───
  route(ROUTES.SEARCH, <PAGES.Search />, "Search"),
  route(ROUTES.PASSWORD, <PAGES.Password />, "Password"),
  route(ROUTES.CONTENT_STATS, <PAGES.ContentStats />, "Content Statistics"),
  route(ROUTES.ADMINS_STATS, <PAGES.AdminsStats />, "Admin Statistics"),
  route(ROUTES.GENERATED_EVENTS, <PAGES.GeneratedDataEvents />, "Generated Events"),
  route(ROUTES.CHAT_SUPPORT, <PAGES.ChatSupport />, "Chat Support"),
];

// Legacy compatibility: some callers may still import the old collection name.
export const MY_ROUTES_COLLECTION: RouteDef[] = [
  ...PUBLIC_ROUTES,
  ...PROTECTED_ROUTES,
];
